import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NgControl, UntypedFormControl } from '@angular/forms';
import { MAT_SELECT_CONFIG, MatSelect } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Subscription } from 'rxjs';
import { COUNTRY_DIAL_CODES, CountryDialCodeDto } from '../../common/constants/country-dial-codes';
import { IconNames } from '../../common/constants/icon-names';
import { AutoUnsubscribe, markRequiredControlWithAsteriskSymbol } from '../../common';
import { IntrackFeatureModule } from '../../intrack-feature.module';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-country-code',
  imports: [IntrackFeatureModule, TranslateModule, NgxMatSelectSearchModule, SvgIconComponent],
  templateUrl: './country-code.component.html',
  styleUrls: ['./country-code.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'mat-mdc-country-code-select-panel' },
    },
  ],
})
@AutoUnsubscribe
export class CountryCodeComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() label: string;
  @Input() isMiniForm = false;
  @Input() withEmptyOption = false;
  @Input() testId = 'countryCode';
  @Input() showClearButton = false;
  @Input() disableField = false;
  @Input() tooltipText?: string;
  @Output() selected = new EventEmitter<CountryDialCodeDto>();

  @ViewChild(MatSelect) matSelect: MatSelect;

  searchCountryCodeControl: UntypedFormControl = new UntypedFormControl();

  countryCodeControl: UntypedFormControl = new UntypedFormControl();

  filteredCountryDialCodes = COUNTRY_DIAL_CODES;
  iconNames = IconNames;

  isFieldRequired = markRequiredControlWithAsteriskSymbol;

  private readonly defaultCountryCode = COUNTRY_DIAL_CODES[0];
  private readonly defaultDialCodeForUSA = COUNTRY_DIAL_CODES.find(
    (dialCode: CountryDialCodeDto) => dialCode.countryDialCode === 'US',
  );
  private readonly subscriptions: Subscription[] = [];

  constructor(@Self() public ngControl: NgControl) {
    ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.initCountryCodeValueChangesSubscription();
    this.initSearchCountryCodeValueChangesSubscription();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disableField?.currentValue) {
      this.countryCodeControl.disable();
    } else {
      this.countryCodeControl.enable();
    }
  }

  onChange: (value: string) => string;

  onTouched: () => void;

  writeValue(countryCodeId: string): void {
    if (countryCodeId) {
      countryCodeId = countryCodeId.replace('+', '');

      const countryCode = this.getCountryDialCodeDtoFromId(countryCodeId);
      this.countryCodeControl.setValue(countryCode);
    }
  }

  registerOnChange(fn: () => string): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onSelected(): void {
    this.selected.emit(this.countryCodeControl.value);
  }

  onClearButtonClick(event: MouseEvent): void {
    event.stopPropagation();
    this.countryCodeControl.setValue(null);
  }

  private initCountryCodeValueChangesSubscription(): void {
    const subscription = this.countryCodeControl.valueChanges.subscribe((countryCode: CountryDialCodeDto) => {
      this.onChange(countryCode?.id);
    });

    this.subscriptions.push(subscription);
  }

  private initSearchCountryCodeValueChangesSubscription(): void {
    this.subscriptions.push(
      this.searchCountryCodeControl.valueChanges.subscribe((query: string) =>
        this.updateFilteredCountryDialCodes(query),
      ),
    );
  }

  private updateFilteredCountryDialCodes(query: string): void {
    if (query.startsWith('00') && query.length > 2) {
      query = query.substring(2).toLocaleLowerCase();
    } else if (query.startsWith('0') && query.length > 1) {
      query = query.substring(1).toLocaleLowerCase();
    } else {
      query = query.toLocaleLowerCase();
    }

    this.filteredCountryDialCodes = COUNTRY_DIAL_CODES.filter((code: CountryDialCodeDto) =>
      code.name.toLocaleLowerCase().includes(query),
    );
  }

  private getCountryDialCodeDtoFromId(id: string): CountryDialCodeDto {
    return (
      COUNTRY_DIAL_CODES.find((code: CountryDialCodeDto) => {
        const isDialCodeSameAsUS = id === this.defaultDialCodeForUSA.id;

        if (isDialCodeSameAsUS && code.id === id) {
          return code.countryDialCode === this.defaultDialCodeForUSA.countryDialCode;
        }

        return code.id === id;
      }) || this.defaultCountryCode
    );
  }
}
